// Cipher's first-person voice (PURE, dependency-free). Derives up to 3 sentences of what
// the mind did while the founder was away — from REAL moments only. Spec §6 / M1 voice.
//
// HONESTY: every line maps to a real signal (supersede / confidence-delta / conflict /
// a real waiting priority). If nothing real moved, the voice is honestly empty:
// "Nothing moved since yesterday's close — my standing view holds." No card, no header.
//
// Ordering follows Bible Law #2 (biggest cognitive change first): supersede >
// confidence-delta > conflict > a decision waiting.
import { versionsForSubject, detectSupersede, detectConfidenceDelta } from "./moments.js";

export const EMPTY_VOICE = "Nothing moved since yesterday's close — my standing view holds.";

// Collect the real moments across already-loaded data (per subject).
export function collectRealMoments({ outcomes = [], evaluations = [], journalEvents = [] } = {}) {
  const subjects = [...new Set(outcomes.map((o) => o.subject_ref_id).filter(Boolean))];
  const supersedes = [];
  const confidenceDeltas = [];
  for (const s of subjects) {
    const vs = versionsForSubject(outcomes, s);
    const sup = detectSupersede(vs, journalEvents.filter((e) => e.subject_ref_id === s || !e.subject_ref_id));
    if (sup.present) supersedes.push({ subject: s, ...sup });
    const cd = detectConfidenceDelta(vs);
    if (cd.present) confidenceDeltas.push({ subject: s, ...cd });
  }
  const conflicts = evaluations
    .filter((e) => e.diagnostics && e.diagnostics.cross_producer_agreement && e.diagnostics.cross_producer_agreement.disagreements > 0)
    .map((e) => ({ cohort_key: e.cohort_key, ...e.diagnostics.cross_producer_agreement }));
  return { supersedes, confidenceDeltas, conflicts };
}

function label(subject, labels) {
  return (labels && labels[subject]) || subject;
}

// Build the voice. Returns { empty:boolean, lead, lines:[...], emptyLine }.
export function deriveVoiceLines({ supersedes = [], confidenceDeltas = [], conflicts = [], priority = null, pendingCount = 0, labels = {} } = {}, { max = 3 } = {}) {
  const lines = [];
  const push = (text, kind, subject) => {
    if (lines.length < max) lines.push({ text, kind, subject: subject || null });
  };

  for (const s of supersedes) push(`I let go of my earlier read on ${label(s.subject, labels)} — I changed my mind.`, "supersede", s.subject);
  for (const c of confidenceDeltas) push(`I ${c.direction === "increased" ? "raised" : "lowered"} my confidence in ${label(c.subject, labels)}.`, "confidence", c.subject);
  for (const cf of conflicts) push(`I found conflicting signals in ${cf.cohort_key || "one cohort"} — worth a look.`, "conflict", null);
  if (priority && priority.item_id) {
    const more = pendingCount > 1 ? ` (and ${pendingCount - 1} more)` : "";
    push(`One decision is waiting for you${more}.`, "priority", priority.item_id);
  }

  if (lines.length === 0) {
    return { empty: true, lead: "Since you left yesterday", lines: [], emptyLine: EMPTY_VOICE };
  }
  return { empty: false, lead: "Since you left yesterday", lines, emptyLine: EMPTY_VOICE };
}
